import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { useCancelToken } from 'component/core/cancel-token';
import {
    addBillFraud,
    addOperationFraud,
    deleteBillFraud,
    deleteOperationFraud,
    getFraudActionList
} from 'component/api/main';

import { IFraud } from 'component/api/types/api/main/get-fraud-action-list/get/code-200';

const useRequestFraud = () => {
    const token = useCancelToken();

    const [data, setData] = useState<Array<IFraud>>([]);
    const [pending, setPending] = useState<boolean>(false);
    const [pendingChange, setPendingChange] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        return () => {
            token.remove();
        };
    }, []);

    const requestFraudList = useCallback((): void => {
        setPending(true);
        setError(null);
        setData([]);

        getFraudActionList({
            cancelToken: token.new()
        })
            .then((resp) => {
                setData(resp);
                setPending(false);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setPending(false);
                    setError(`(${err.response?.status}) ${err.response?.data?.error}` || 'Ошибка обработки данных');
                }
            });
    }, [token]);

    const requestOperationFraudAdd = useCallback((operation_id: number, fraud_action_id: number, comment = '', call_back?: () => void): void => {
        setPendingChange(true);
        setError(null);

        addOperationFraud({
            cancelToken: token.new(),
            data: {
                data: {
                    operation: {
                        id: operation_id,
                        fraud_activity: {
                            fraud_action_id,
                            comment
                        }
                    }
                }
            }
        })
            .then((resp) => {
                if (resp.result === 'success') {
                    setPendingChange(false);
                    call_back && call_back();
                } else {
                    throw resp;
                }
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setPendingChange(false);
                    setError(`(${err.response?.status}) ${err.response?.data?.error}` || 'Ошибка обработки данных');
                }
            });
    }, [token]);

    const requestOperationFraudDelete = useCallback((operation_id: number, fraud_activity_id: number, call_back?: () => void): void => {
        setPendingChange(true);
        setError(null);

        deleteOperationFraud({
            cancelToken: token.new(),
            data: {
                data: {
                    operation_id,
                    fraud_activity_id
                }
            }
        })
            .then((resp) => {
                if (resp.result === 'success') {
                    setPendingChange(false);
                    call_back && call_back();
                } else {
                    throw resp;
                }
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setPendingChange(false);
                    setError(`(${err.response?.status}) ${err.response?.data?.error}` || 'Ошибка обработки данных');
                }
            });
    }, [token]);

    const requestBillFraudAdd = useCallback((bill_id: number, fraud_action_id: number, comment = '', call_back?: () => void): void => {
        setPendingChange(true);
        setError(null);

        addBillFraud({
            cancelToken: token.new(),
            data: {
                data: {
                    bill: {
                        id: bill_id,
                        fraud_activity: {
                            fraud_action_id,
                            comment
                        }
                    }
                }
            }
        })
            .then((resp) => {
                if (resp.result === 'success') {
                    setPendingChange(false);
                    call_back && call_back();
                } else {
                    throw resp;
                }
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setPendingChange(false);
                    setError(`(${err.response?.status}) ${err.response?.data?.error}` || 'Ошибка обработки данных');
                }
            });
    }, [token]);

    const requestBillFraudDelete = useCallback((bill_id: number, fraud_activity_id: number, call_back?: () => void): void => {
        setPendingChange(true);
        setError(null);

        deleteBillFraud({
            cancelToken: token.new(),
            data: {
                data: {
                    bill_id,
                    fraud_activity_id
                }
            }
        })
            .then((resp) => {
                if (resp.bill_id) {
                    setPendingChange(false);
                    call_back && call_back();
                } else {
                    throw resp;
                }
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setPendingChange(false);
                    setError(`(${err.response?.status}) ${err.response?.data?.error}` || 'Ошибка обработки данных');
                }
            });
    }, [token]);

    return {
        data,
        pending,
        pendingChange,
        error,
        requestFraudList,
        requestOperationFraudAdd,
        requestOperationFraudDelete,
        requestBillFraudAdd,
        requestBillFraudDelete
    };
};

export default useRequestFraud;
